import pickBy from 'lodash/pickBy'
import {
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
  GeckoRectangularManway,
  GeckoRectangularManwayJSON,
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  compassOrientationFormField,
  tminFormField,
} from 'types/assetModel/commonFields'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoAnnularRing,
  GeckoAnnularRingJSON,
} from 'types/assetModel/features/geckoAnnularRing'
import {
  GeckoAnnularRingWeldLine,
  GeckoAnnularRingWeldLineJSON,
} from 'types/assetModel/features/geckoAnnularRingWeldLine'
import {
  GeckoManway,
  GeckoManwayJSON,
} from 'types/assetModel/features/geckoManway'
import {
  GeckoNozzle,
  GeckoNozzleJSON,
} from 'types/assetModel/features/geckoNozzle'
import {
  GeckoPlate,
  GeckoPlateJSON,
} from 'types/assetModel/features/geckoPlate'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { mergeMaps } from 'utils/mergeMaps'
import { parseNumber } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export type TankFloorAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.TankFloor
  radius: number
  features: Record<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoPlateJSON
    | GeckoNozzleJSON
    | GeckoManwayJSON
    | GeckoRectangularManwayJSON
    | GeckoAnnularRingJSON
    | GeckoAnnularRingWeldLineJSON
  >
  scene_config: SceneConfig
  component_bounds: Record<string, number>
  tmin: number | undefined
  nominal: {
    base_thickness: number | undefined
  }
  compass_orientation: number | undefined
}

export const TankFloorAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.Plate,
  GeckoFeatureModelType.VerticalWeld,
  GeckoFeatureModelType.HorizontalWeld,
  GeckoFeatureModelType.Nozzle,
  GeckoFeatureModelType.Manway,
  GeckoFeatureModelType.RectangularManway,
  GeckoFeatureModelType.FixedSensor,
  GeckoFeatureModelType.AnnularRing,
  GeckoFeatureModelType.AnnularRingWeldLine,
]

export const NewTankFloorAssetModelJSON = {
  name: 'New Tank Floor',
  type: GeckoComponentModelType.TankFloor,
  radius: 0,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  tmin: undefined,
  nominal: {
    base_thickness: undefined,
  },
  compass_orientation: undefined,
} as TankFloorAssetModelJSON

export class GeckoTankFloor extends GeckoEditableComponent {
  type: GeckoComponentModelType.TankFloor
  radius: GeckoMeasurement
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  plates: Map<string, GeckoPlate>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  verticalWelds: Map<string, GeckoVerticalWeld>
  nozzles: Map<string, GeckoNozzle>
  manways: Map<string, GeckoManway>
  rectangularManways: Map<string, GeckoRectangularManway>
  annularRings: Map<string, GeckoAnnularRing>
  annularRingWeldLines: Map<string, GeckoAnnularRingWeldLine>
  features: Map<
    string,
    | GeckoPlate
    | GeckoHorizontalWeld
    | GeckoVerticalWeld
    | GeckoNozzle
    | GeckoManway
    | GeckoRectangularManway
    | GeckoAnnularRing
    | GeckoAnnularRingWeldLine
  >
  baseNominalThickness: GeckoMeasurement | undefined
  tmin: GeckoMeasurement | undefined
  compassOrientation: number | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    plates: Map<string, GeckoPlate>,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    nozzles: Map<string, GeckoNozzle>,
    manways: Map<string, GeckoManway>,
    rectangularManways: Map<string, GeckoRectangularManway>,
    annularRings: Map<string, GeckoAnnularRing>,
    annularRingWeldLines: Map<string, GeckoAnnularRingWeldLine>,
    baseNominalThickness: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined,
    compassOrientation: number | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.TankFloor
    this.radius = radius
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.baseNominalThickness = baseNominalThickness
    this.tmin = tmin
    this.compassOrientation = compassOrientation

    // Features
    this.plates = plates
    this.horizontalWelds = horizontalWelds
    this.verticalWelds = verticalWelds
    this.nozzles = nozzles
    this.manways = manways
    this.rectangularManways = rectangularManways
    this.annularRings = annularRings
    this.annularRingWeldLines = annularRingWeldLines

    this.features = mergeMaps(
      plates,
      horizontalWelds,
      verticalWelds,
      nozzles,
      manways,
      rectangularManways,
      annularRings,
      annularRingWeldLines
    )
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }

  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }

  public get nozzlesArray() {
    return Array.from(this.nozzles.values())
  }

  public get manwaysArray() {
    return Array.from(this.manways.values())
  }

  public get rectangularManwaysArray() {
    return Array.from(this.rectangularManways.values())
  }

  public get annularRingsArray() {
    return Array.from(this.annularRings.values())
  }

  public get annularRingWeldLinesArray() {
    return Array.from(this.annularRingWeldLines.values())
  }

  /**
   * Inner radius of the innermost annular ring: the boundary of the center-plate
   * region, outside of which the rings supersede the plates and welds beneath them.
   * Rings with a non-positive inner radius (not yet positioned) are ignored so they
   * cannot hide the whole floor. Undefined when the floor has no positioned rings.
   */
  public get insideAnnularRingsRadius(): GeckoMeasurement | undefined {
    const innerRadii = this.annularRingsArray
      .map((ring) => ring.innerRadius.convertTo(GeckoMeasurementUnit.Meters))
      .filter((radius) => radius.num > 0)
    if (innerRadii.length === 0) return undefined
    return GeckoMeasurement.Min(...innerRadii)
  }

  public static CreateFromJSON(id: string, json: TankFloorAssetModelJSON) {
    const name = json.name as string
    const radius = GeckoMeasurement.CreateFromValue(
      json.radius as number,
      GeckoMeasurementUnit.Meters
    )

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config as SceneConfig

    const featureData = json.features as Record<
      string,
      | GeckoPlateJSON
      | GeckoHorizontalWeldJSON
      | GeckoVerticalWeldJSON
      | GeckoManwayJSON
      | GeckoRectangularManwayJSON
      | GeckoNozzleJSON
      | GeckoAnnularRingJSON
      | GeckoAnnularRingWeldLineJSON
    >

    const childPlateJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Plate
    })
    const plates = GeckoPlate.CreateMapFromJSON(
      id,
      childPlateJSON as Record<string, GeckoPlateJSON>
    )

    const childHorizontalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.HorizontalWeld
    })
    const horizontalWelds = GeckoHorizontalWeld.CreateMapFromJSON(
      id,
      childHorizontalWeldJSON as Record<string, GeckoHorizontalWeldJSON>
    )

    const childVerticalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.VerticalWeld
    })
    const verticalWelds = GeckoVerticalWeld.CreateMapFromJSON(
      id,
      childVerticalWeldJSON as Record<string, GeckoVerticalWeldJSON>
    )

    const childNozzleJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Nozzle
    })
    const nozzles = GeckoNozzle.CreateMapFromJSON(
      id,
      childNozzleJSON as Record<string, GeckoNozzleJSON>
    )

    const childManwayJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Manway
    })
    const manways = GeckoManway.CreateMapFromJSON(
      id,
      childManwayJSON as Record<string, GeckoManwayJSON>
    )

    const childRectangularManwayJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.RectangularManway
    })
    const rectangularManways = GeckoRectangularManway.CreateMapFromJSON(
      id,
      childRectangularManwayJSON as Record<string, GeckoRectangularManwayJSON>
    )

    const childAnnularRingJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.AnnularRing
    })
    const annularRings = GeckoAnnularRing.CreateMapFromJSON(
      id,
      childAnnularRingJSON as Record<string, GeckoAnnularRingJSON>,
      radius // Pass tank floor radius for default outer radius
    )

    const childAnnularRingWeldLineJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.AnnularRingWeldLine
    })
    const annularRingWeldLines = GeckoAnnularRingWeldLine.CreateMapFromJSON(
      id,
      childAnnularRingWeldLineJSON as Record<
        string,
        GeckoAnnularRingWeldLineJSON
      >
    )

    const nominalJSON = json.nominal as Record<string, number>
    const parsedBaseNominalThickness = parseNumber(nominalJSON.base_thickness)
    const baseNominalThickness =
      parsedBaseNominalThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedBaseNominalThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined
    const parsedTmin = parseNumber(json.tmin)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    const compassOrientation = parseNumber(json.compass_orientation)
    return new GeckoTankFloor(
      id,
      json,
      name,
      radius,
      componentBounds,
      sceneConfig,
      plates,
      horizontalWelds,
      verticalWelds,
      nozzles,
      manways,
      rectangularManways,
      annularRings,
      annularRingWeldLines,
      baseNominalThickness,
      tmin,
      compassOrientation
    )
  }

  public asJSON(): TankFloorAssetModelJSON {
    return generateTankFloor(
      this.name,
      this.radius,
      this.baseNominalThickness,
      this.tmin,
      this.compassOrientation,
      this.json as TankFloorAssetModelJSON
    )
  }
}

const generateTankFloor = (
  name: string,
  radius: GeckoMeasurement,
  baseNominalThickness: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined,
  compassOrientation: number | undefined,
  otherTankFloorData: TankFloorAssetModelJSON = NewTankFloorAssetModelJSON
): TankFloorAssetModelJSON => {
  const floorRadiusMeters = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const floorDiameterMeters = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .scale(2)
    .toPrecision(assetModelPrecision).num

  const nominalThicknessMeters =
    baseNominalThickness !== undefined
      ? baseNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          ?.toPrecision(assetModelPrecision).num
      : undefined
  const tminMeters =
    tmin !== undefined
      ? tmin
          .convertTo(GeckoMeasurementUnit.Meters)
          ?.toPrecision(assetModelPrecision).num
      : undefined

  return {
    name: name,
    type: GeckoComponentModelType.TankFloor,
    radius: floorRadiusMeters,
    features: otherTankFloorData.features,
    scene_config: otherTankFloorData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: floorDiameterMeters,
      y_end: floorDiameterMeters,
    },
    tmin: tminMeters,
    nominal: { base_thickness: nominalThicknessMeters },
    compass_orientation: compassOrientation,
  }
}

export const TankFloorAssetModelFormFields = (
  tankFloorJSON: TankFloorAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Floor Name',
      value: tankFloorJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    // Diameter is the industry standard measurement for tanks, so convert to radius and back
    {
      dataKey: 'radius',
      label: 'Diameter',
      value: tankFloorJSON.radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'base_thickness',
      label: 'Nominal Thickness',
      value: tankFloorJSON.nominal.base_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    tminFormField(tankFloorJSON.tmin),
    compassOrientationFormField(tankFloorJSON.compass_orientation),
  ]
}

export const TankFloorJSONFromFormFields = (
  component: TankFloorAssetModelJSON,
  formFields: FormField[]
) => {
  const name = getFormFieldByDataKey(formFields, 'name')?.value ?? ''
  const radius = getFormFieldByDataKey(formFields, 'radius')
  const baseNominalThickness = getFormFieldByDataKey(
    formFields,
    'base_thickness'
  )
  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )
  const tmin = getFormFieldByDataKey(formFields, 'tmin')
  return generateTankFloor(
    name as string,
    GeckoMeasurement.CreateFromValue(
      (radius?.value as number) / 2, //radius is diameter
      GeckoMeasurementUnit.Meters
    ),
    baseNominalThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          baseNominalThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    tmin?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          tmin?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    compassOrientation,
    component
  )
}
